import React, { memo, useEffect, useState } from 'react';
import { Platform, StyleSheet, View, Dimensions, Pressable, PressableProps } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import * as Animatable from 'react-native-animatable';
import { useDispatch, useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { CONTEXT_SIZE_TOGGLE } from '@reduxLocal/actions/types';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Text } from 'tamagui';
import { useTranslation } from 'react-i18next';

interface ContextActions {
  onPress: () => void;
  buttonText?: string;
  disabled?: boolean;
  icon?: JSX.Element | string;
  defaultColor?: string;
  onPressColor?: string;
  onHoverColor?: string;
  portrait?: boolean;
  outline?: boolean;
  customStyle?: Object,
  disableExpand?: boolean,
  iconColor?: string,
  textColor?: string,
  activeComponent?: string,
  name?: string,
  index?: number,
  lastItem?: boolean,
  selectedColor: string,
  smallButton?: boolean,
  iconSize?: number,
  wrap?: boolean, // Added wrap prop
  /**
   * Props for the container of the button, default values are maxHeight: 70, alignSelf: 'stretch'
   * @param containerProps
   */
  containerProps?: PressableProps
}

const ContextAction: React.FC<ContextActions> = ({
  onPress,
  disableExpand,
  buttonText,
  disabled = false,
  icon,
  iconColor,
  defaultColor = 'white',
  onPressColor = '#99DBF7',
  portrait = false,
  outline = false,
  customStyle,
  textColor,
  activeComponent,
  name,
  selectedColor,
  iconSize,
  index,
  lastItem,
  smallButton,
  wrap = false, // Default wrap to false
  containerProps
}) => {
  const [buttonColorState, setButtonColorState] = useState(activeComponent === name ? selectedColor : defaultColor);
  const [loaded, setLoaded] = useState(false);
  const minimized = useSelector((state: DefaultRootState) => state.UI.contextMinimized);
  const dispatch = useDispatch();
  const theme = themeSelector();
  const { t } = useTranslation();

  const actionStyleArray = [
    ActionStyles.button,
    outline && ActionStyles.outlineButton,
    { backgroundColor: buttonColorState },
    disabled && ActionStyles.disabledButton,
    Platform.OS === 'web' ? ActionStyles.webButton : ActionStyles.mobileButton,
    index === 0 && {
      borderTopLeftRadius: 20,
      borderBottomLeftRadius: 20
    },
    lastItem && {
      borderTopRightRadius: 20,
      borderBottomRightRadius: 20
    },
    minimized && {
      borderRadius: 20,
      marginLeft: 15,
      marginVertical: 5,
    }
  ].filter(Boolean);

  const actionStyle = StyleSheet.flatten(actionStyleArray);

  const handlePressIn = () => {
    if (loaded) {
      setButtonColorState(onPressColor);
    }
  };

  const handlePressOut = () => {
    if (loaded) {
      setButtonColorState(activeComponent === name ? selectedColor : defaultColor);
    }
  };


  return (
    <Pressable
      style={{ 
        flex: 1, 
        minWidth: smallButton ? 80 : 120, // Updated to match ActionArray minimum widths
        minHeight: Platform.select({ web: minimized ? 90 : 70, default: 50 }), 
        maxHeight: 90, 
        borderRadius: 20,
        alignSelf: 'stretch', 
        maxWidth: wrap ? '100%' : undefined // Set maxWidth to 100% when parent is wrapped
      }}
      onPress={() => { if (minimized && !disableExpand) { dispatch({ type: CONTEXT_SIZE_TOGGLE }) }; onPress() }}
      disabled={disabled}
      onPressIn={handlePressIn}
      onPressOut={handlePressOut}
      {...containerProps}>
      {({ hovered }) => (
        <Animatable.View 
          transition={'backgroundColor'} 
          style={{
            ...actionStyle,
            backgroundColor: hovered ? selectedColor : (activeComponent === name ? selectedColor : defaultColor),
            shadowRadius: 20,
            shadowOpacity: .2,
            shadowColor: theme ? undefined : "white",
            flex: 1,
            ...(customStyle || {}),
            alignSelf: 'stretch',
          }}>
        <View
            style={{ flexDirection: 'column', flex: 1, minWidth: '100%', minHeight: '100%', justifyContent: 'center', alignItems: 'center', gap: 10 }}
        >
          {icon && (
              <View style={ActionStyles.iconContainer}>
              {typeof icon === 'string' ? (
                <>{<AntDesign name={icon as any} size={iconSize ||30} color={iconColor || 'black'} />}</>
              ) : (
                icon
              )}
            </View>
          )}
            <Text style={{
              ...ActionStyles.buttonText,
              top: Platform.select({web: -3, default: -10}),
              fontSize: 10,
              color: textColor || "white"
            }}>{t(buttonText)}</Text>
        </View>
      </Animatable.View>
      )}
    </Pressable>

  );
};

const ActionStyles = StyleSheet.create({
  button: {
    maxHeight: 90,
    alignSelf:'stretch',
    minWidth: "100%",
    minHeight: 65,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#58BFEA',
    shadowRadius: 10,
    shadowOpacity: 0.2,
  },
  highlightButton: { backgroundColor: 'rgb(90,192,234)' },
  disabledButton: {
    backgroundColor: '#848484',
  },
  outlineButton: {
    backgroundColor: 'white',
    borderWidth: 1,
  },
  buttonText: {
    fontWeight: 'bold',
    textAlign:"center"
  },
  iconContainer: {
  },
  portraitButton: {
    flexDirection: 'column',
  },
  webButton: {
    padding: 10,
  },
  mobileButton: {
    minWidth: "100%",
  },
  rotateButton: {
    flexDirection: 'column',
    maxHeight: 80,
  },
})

export default memo(ContextAction);
